import { TFunction } from 'i18next';
import {
  ProfessionalPath,
  ContactPreference,
  WorkSector,
  Title,
  Gender,
  MembershipType,
} from '../types/member';
import { AdministrativeStatus, InstitutionMembershipCategory } from '../types/institution';
import { tryNormalizeEnumValue } from './enumNormalize';
import { formatChangeRequestValue } from './formatChangeValue';

/** Maps change-request field identifiers to i18n label keys. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  ProfessionalPath: 'profile.professionalPath',
  professionalPath: 'profile.professionalPath',
  Residential: 'profile.residentialAddress',
  residential: 'profile.residentialAddress',
  ResidentialAddress: 'profile.residentialAddress',
  Name: 'common.name',
  FirstName: 'common.firstName',
  LastName: 'common.lastName',
  FirstNameEn: 'register.firstNameEnglish',
  FirstNameGr: 'register.firstNameGreek',
  LastNameEn: 'register.lastNameEnglish',
  LastNameGr: 'register.lastNameGreek',
  firstNameEn: 'register.firstNameEnglish',
  firstNameGr: 'register.firstNameGreek',
  lastNameEn: 'register.lastNameEnglish',
  lastNameGr: 'register.lastNameGreek',
  Email: 'common.email',
  email: 'common.email',
  Phone: 'common.phone',
  phone: 'common.phone',
  Mobile: 'common.mobile',
  mobile: 'common.mobile',
  Website: 'register.workplaceWebsite',
  website: 'register.workplaceWebsite',
  Title: 'register.titleOption',
  title: 'register.titleOption',
  Gender: 'register.gender',
  gender: 'register.gender',
  DateOfBirth: 'register.dateOfBirth',
  dateOfBirth: 'register.dateOfBirth',
  ContactPreference: 'register.contactPreference',
  contactPreference: 'register.contactPreference',
  WorkSector: 'register.workSector',
  workSector: 'register.workSector',
  OrganisationName: 'profile.organisationName',
  organisationName: 'profile.organisationName',
  ProfessionalTitle: 'profile.professionalTitle',
  professionalTitle: 'profile.professionalTitle',
  ProfessionalTitleEn: 'profile.professionalTitle',
  ProfessionalTitleGr: 'profile.professionalTitle',
  degreeType: 'register.studies.degreeType',
  DegreeType: 'register.studies.degreeType',
  institutionEn: 'register.studies.institutionEn',
  InstitutionEn: 'register.studies.institutionEn',
  institutionGr: 'register.studies.institutionGr',
  InstitutionGr: 'register.studies.institutionGr',
  startYear: 'register.studies.startYear',
  StartYear: 'register.studies.startYear',
  completionYear: 'register.studies.completionYear',
  CompletionYear: 'register.studies.completionYear',
  duration: 'register.studies.duration',
  Duration: 'register.studies.duration',
  titleEn: 'register.studies.studyTitleEn',
  titleGr: 'register.studies.studyTitleGr',
  ProfessionalInstitutionName: 'register.professionalInstitution',
  professionalInstitutionName: 'register.professionalInstitution',
  ProfessionalAddress: 'register.professionalAddress',
  professionalAddress: 'register.professionalAddress',
  ProfessionalAddressEn: 'register.professionalAddress',
  ProfessionalAddressGr: 'register.professionalAddress',
  ProfessionalCity: 'register.institutionCity',
  ProfessionalCityEn: 'register.institutionCity',
  ProfessionalCityGr: 'register.institutionCity',
  ProfessionalPostalCode: 'register.institutionPostalCode',
  professionalPostalCode: 'register.institutionPostalCode',
  ProfessionalEmail: 'register.professionalEmail',
  professionalEmail: 'register.professionalEmail',
  ProfessionalPhone: 'register.professionalPhone',
  professionalPhone: 'register.professionalPhone',
  MainCommittee: 'register.committees.main',
  mainCommittee: 'register.committees.main',
  AdditionalCommittees: 'register.committees.secondary',
  additionalCommittees: 'register.committees.secondary',
  Studies: 'profile.sectionStudies',
  studies: 'profile.sectionStudies',
  IsGraduateStudent: 'register.isGraduateStudent',
  isGraduateStudent: 'register.isGraduateStudent',
  IsRetired: 'register.isRetired',
  isRetired: 'register.isRetired',
  IsVolunteer: 'profile.isVolunteer',
  isVolunteer: 'profile.isVolunteer',
  MembershipType: 'profile.membershipType',
  membershipType: 'profile.membershipType',
  MembershipCategory: 'register.membershipCategory',
  membershipCategory: 'register.membershipCategory',
  AdministrativeStatus: 'institution.administrativeStatusLabel',
  administrativeStatus: 'institution.administrativeStatusLabel',
  InstitutionName: 'register.institutionName',
  institutionName: 'register.institutionName',
};

const ENUM_FIELD_MAP: Record<string, Record<string, string | number>> = {
  ProfessionalPath,
  professionalPath: ProfessionalPath,
  ContactPreference,
  contactPreference: ContactPreference,
  WorkSector,
  workSector: WorkSector,
  Title,
  title: Title,
  Gender,
  gender: Gender,
  MembershipType,
  membershipType: MembershipType,
  MembershipCategory: InstitutionMembershipCategory,
  membershipCategory: InstitutionMembershipCategory,
  AdministrativeStatus,
  administrativeStatus: AdministrativeStatus,
};

function labelForEnum(fieldName: string, numeric: number, t: TFunction): string | null {
  if (fieldName === 'ProfessionalPath' || fieldName === 'professionalPath') {
    const map: Record<number, string> = {
      [ProfessionalPath.ActiveMuseumProfessional]: t('register.professionalPathActive'),
      [ProfessionalPath.Student]: t('register.professionalPathStudent'),
      [ProfessionalPath.InterestedNonProfessional]: t('register.professionalPathInterested'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'WorkSector' || fieldName === 'workSector') {
    const map: Record<number, string> = {
      [WorkSector.Museology]: t('workSector.museology'),
      [WorkSector.Education]: t('workSector.education'),
      [WorkSector.Documentation]: t('workSector.documentation'),
      [WorkSector.Conservation]: t('workSector.conservation'),
      [WorkSector.Exhibition]: t('workSector.exhibition'),
      [WorkSector.Management]: t('workSector.management'),
      [WorkSector.Communication]: t('workSector.communication'),
      [WorkSector.Other]: t('workSector.other'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'MembershipCategory' || fieldName === 'membershipCategory') {
    const map: Record<number, string> = {
      [InstitutionMembershipCategory.ActiveI]: t('register.budgetLessThan30k'),
      [InstitutionMembershipCategory.ActiveII]: t('register.budget30kTo100k'),
      [InstitutionMembershipCategory.ActiveIII]: t('register.budget100kTo1M'),
      [InstitutionMembershipCategory.ActiveIV]: t('register.budget1MTo5M'),
      [InstitutionMembershipCategory.ActiveV]: t('register.budget5MTo10M'),
      [InstitutionMembershipCategory.ActiveVI]: t('register.budgetMoreThan10M'),
      [InstitutionMembershipCategory.Benefactor]: t('register.budgetBenefactor'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'Title' || fieldName === 'title') {
    const map: Record<number, string> = {
      [Title.Mr]: t('register.titleMr'),
      [Title.Ms]: t('register.titleMs'),
      [Title.Mrs]: t('register.titleMrs'),
      [Title.Dr]: t('register.titleDr'),
      [Title.Prof]: t('register.titleProf'),
      [Title.Other]: t('register.titleOther'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'Gender' || fieldName === 'gender') {
    const map: Record<number, string> = {
      [Gender.Male]: t('profile.gender.male'),
      [Gender.Female]: t('profile.gender.female'),
      [Gender.Other]: t('profile.gender.other'),
      [Gender.PreferNotToSay]: t('profile.gender.preferNotToSay'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'AdministrativeStatus' || fieldName === 'administrativeStatus') {
    const map: Record<number, string> = {
      [AdministrativeStatus.Public]: t('institution.administrativeStatus.public'),
      [AdministrativeStatus.Private]: t('institution.administrativeStatus.private'),
      [AdministrativeStatus.Mixed]: t('institution.administrativeStatus.mixed'),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'ContactPreference' || fieldName === 'contactPreference') {
    const map: Record<number, string> = {
      [ContactPreference.Personal]: t('register.contactPreferencePersonal', {
        defaultValue: 'Personal',
      }),
      [ContactPreference.Professional]: t('register.contactPreferenceProfessional', {
        defaultValue: 'Professional',
      }),
    };
    return map[numeric] ?? null;
  }
  if (fieldName === 'MembershipType' || fieldName === 'membershipType') {
    const map: Record<number, string> = {
      [MembershipType.Regular]: t('membershipType.regular'),
      [MembershipType.Student]: t('membershipType.student'),
      [MembershipType.Retired]: t('membershipType.retired'),
      [MembershipType.Honorary]: t('membershipType.honorary', { defaultValue: 'Honorary' }),
    };
    return map[numeric] ?? null;
  }
  return null;
}

export function formatChangeRequestFieldName(fieldName: string, t: TFunction): string {
  if (!fieldName) return '—';
  const key = FIELD_LABEL_KEYS[fieldName];
  if (key) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return fieldName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
}

export function formatChangeRequestFieldValue(
  fieldName: string,
  raw: string | null | undefined,
  t: TFunction
): string {
  if (raw == null || raw === '') return '—';
  const trimmed = raw.trim();

  const enumObj = ENUM_FIELD_MAP[fieldName];
  if (enumObj) {
    const normalized = tryNormalizeEnumValue(enumObj, trimmed);
    if (normalized != null && typeof normalized === 'number') {
      const label = labelForEnum(fieldName, normalized, t);
      if (label) return label;
    }
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum)) {
      const label = labelForEnum(fieldName, asNum, t);
      if (label) return label;
    }
  }

  return formatChangeRequestValue(raw, t);
}
